import { Injectable } from "@angular/core";
import { DataAccess } from "../dataaccess/data.access";
import { EventI } from "../pojo/EventI";
import { Conflicts, EventImportSaveResponseDto, ReservationSaveResponse } from "../modules/import/import.service";
import { S25Promise } from "../util/core/s25.promise";

export interface EventBulkSaveRequestDto {
    overrideFolderId?: number;
    overrideCabinetId?: number;
    fallbackFolderId?: number;
    fallbackCabinetId?: number;
    allowShared?: boolean;
    allowBlackoutOverride?: boolean;
    allowClosedHoursOverride?: boolean;
    allowBlockedByOverride?: boolean;
    moveConflictsToPreferences?: boolean;
    events: EventI[];
}

export interface EventBulkSaveDenial {
    objectType?: string;
    objectId?: number;
    reason?: string;
}

export interface EventBulkSaveResponseDto extends EventImportSaveResponseDto {
    warnings?: string[];
    denials?: EventBulkSaveDenial[];
    reservationSaveResponse?: ReservationSaveResponse & { conflicts?: Conflicts };
}

@Injectable({ providedIn: "root" })
export class EventBulkSaveService {
    /**
     * Saves a batch of events. A partial payload is a patch everywhere except
     * reservations and their space/resource assignments, which are a scoped replace.
     *
     * - Event core columns (event type, account, head counts, title, name, start/end, last-mod)
     *   use COALESCE: a null in the payload keeps the stored value. Columns outside that list
     *   are never written.
     * - Custom attributes, texts, contacts, and registration config upsert by
     *   (eventId, natural key). A matching key is overwritten; a key absent from the payload
     *   is left alone and never deleted.
     * - Categories, services, and organizations are insert-if-missing only. Existing rows
     *   cannot be removed by omitting them.
     * - Profiles upsert by (eventId, profile id) or (eventId, ext uid). A profile not in the
     *   payload is left alone.
     * - Reservations (occurrences) are a replace scoped to profiles that appear in the payload.
     *   Sending a profile's reservations makes that list the full set for the profile; an
     *   occurrence left out is deleted. Profiles not mentioned are unaffected.
     * - Space and resource reservations are a replace per occurrence that is sent. The sent
     *   list is the complete assignment; a room or resource not resent is dropped. An empty
     *   or omitted list clears that occurrence's assignments.
     */
    save(request: EventBulkSaveRequestDto) {
        const url = "/events/bulk/save.json";
        const injected = DataAccess.injectCaller(url, "EventBulkSaveService.save");
        return S25Promise.Result<EventBulkSaveResponseDto, { data?: EventBulkSaveResponseDto; status?: number }>(
            DataAccess.post<EventBulkSaveResponseDto>(injected, request),
        );
    }
}
